import React from "react";
import { motion } from "framer-motion";
import { fadeUp, lineParent, lineChild, viewport } from "@/lib/motion";

// Scroll-triggered fade-up wrapper
export function Reveal({ children, className = "", delay = 0, as = "div", ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      variants={fadeUp}
      initial="hidden"
      whileInView="visible"
      viewport={viewport}
      transition={{ delay }}
      className={className}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Small uppercase gold overline / eyebrow
export function Overline({ children, light = false, className = "", index }) {
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <span className="h-px w-8 bg-gold/70" />
      <span className="text-xs sm:text-sm uppercase tracking-[0.25em] font-semibold text-gold">
        {index != null && (
          <span className="text-gold/50 mr-2 font-mono">
            {String(index).padStart(2, "0")}
          </span>
        )}
        {children}
      </span>
    </div>
  );
}

// Masked line-by-line heading reveal.
// lines: array of strings. Optional `gold` array of substrings to accent.
export function MaskedHeading({ lines, className = "", tag = "h1" }) {
  const Tag = motion[tag] || motion.h1;
  return (
    <Tag
      variants={lineParent}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      className={className}
    >
      {lines.map((line, i) => (
        <span key={i} className="line-mask">
          <motion.span variants={lineChild} className="block">
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

// Infinite editorial marquee
export function Marquee({ items, className = "", separator = "✦" }) {
  const content = (
    <>
      {items.map((t, i) => (
        <span key={i} className="inline-flex items-center">
          <span className="mx-8">{t}</span>
          <span className="text-gold/60 text-2xl">{separator}</span>
        </span>
      ))}
    </>
  );
  return (
    <div className={`relative w-full overflow-hidden ${className}`}>
      <div className="marquee-track">
        {content}
        {content}
      </div>
    </div>
  );
}
